import { createContext, ReactNode, useEffect, useState } from "react";
import api from "../services/api";
import { useParams } from "react-router-dom";
import moment from "moment";

export type ShiftType = {
  id: number;
  timeStart: string;
  timeStart2: string;
  timeEnd: string;
  timeEnd2: string;
  weekday: number;
};

export type PluginspaceResponseType = {
  cnpj?: string;
  id: number;
  hash: string;
  name: string;
  type: string;
  typeId: number;
  gmt?: string;
};

type AddressLocationType = {
  cep: string;
  city: string;
  district: string;
  extra: string;
  number: string;
  state: string;
  street: string;
};

export type LocationType = {
  id: number;
  name: string;
  code: string;
  status: boolean;
  type: string;
  cep: string;
  address: AddressLocationType | null;
};

type PluginspaceContextType = {
  token: string;
  setToken: (token: string) => void;
  pluginspaceData: PluginspaceResponseType | undefined;
  setPluginspaceData: (pluginspaceData: PluginspaceResponseType | any) => void;
  getToken: (subdomain?: string) => Promise<void | string>;
  registerAccess: (
    pageName: string,
    cpfIndicator?: string,
    productIndicator?: number,
    urlBrowsed?: string
  ) => Promise<boolean>;
  getThisPluginspace: (
    otherToken?: string
  ) => Promise<void | PluginspaceResponseType>;
  getVerifyShift: () => Promise<boolean>;

  locations: LocationType[];
  getLocations: () => Promise<LocationType[] | any[]>;

  pluginspace: any;
  subDomain: string;
};

type PluginspaceContextProviderProps = {
  children: ReactNode;
};

export const PluginspaceContext = createContext({} as PluginspaceContextType);

export function PluginspaceContextProvider(
  props: PluginspaceContextProviderProps
) {
  const [token, setToken] = useState<string>("");
  const [pluginspaceData, setPluginspaceData] = useState();
  const [pluginspace, setPluginspace] = useState();
  const [shifts, setShifts] = useState<ShiftType[] | null>(null);
  const [subDomain, setSubDomain] = useState<string>("");
  const [locations, setLocations] = useState<LocationType[]>([]);

  useEffect(() => {
    const subdomain = atob(window.location.pathname.split("/")[1]);
    setSubDomain(window.location.pathname.split("/")[1]);
    getToken(subdomain);
  }, []);

  async function getToken(subdomain?: string) {
    const tokenLocal = localStorage.getItem("tk_data");
    const expiryDate = localStorage.getItem("expiry_tk");
    let samePluginspace = false;
    if (
      tokenLocal &&
      JSON.parse(tokenLocal) &&
      JSON.parse(tokenLocal).subdomain &&
      JSON.parse(tokenLocal).subdomain ===
        window.atob(window.location.pathname.split("/")[1])
    ) {
      samePluginspace = true;
    }
    if (
      tokenLocal &&
      JSON.parse(tokenLocal) &&
      JSON.parse(tokenLocal).token &&
      expiryDate &&
      new Date(expiryDate).getTime() > new Date().getTime() &&
      samePluginspace
    ) {
      return JSON.parse(tokenLocal).token;
    } else {
      localStorage.removeItem("locations");
      localStorage.removeItem("orderToVerify");
      localStorage.removeItem("storeAddress");
      localStorage.removeItem("this_pg");
      localStorage.removeItem("should_show_ler_qrcodeButton");
      localStorage.removeItem("qrcodePixPayment");

      return await api
        .post(`/api/token/get-token`, {
          subdomain: subdomain
            ? subdomain
            : window.atob(window.location.pathname.split("/")[1]),
        })
        .then(async (response) => {
          if (response.data) {
            setPluginspaceData(response.data.pluginspace);
            setToken(response.data.accessToken);
            localStorage.setItem(
              "tk_data",
              JSON.stringify({
                token: response.data.accessToken,
                subdomain: window.atob(window.location.pathname.split("/")[1]),
              })
            );
            localStorage.setItem("expiry_tk", response.data.expiratedAt);
            return response.data.accessToken;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }

  async function getThisPluginspace(otherToken?: string) {
    if (!otherToken) {
      otherToken = await getToken();
    }
    const pluginspaceDataLocal = localStorage.getItem("this_pg");
    if (
      pluginspaceDataLocal &&
      window.atob(pluginspaceDataLocal) &&
      JSON.parse(window.atob(pluginspaceDataLocal))
    ) {
      const pluginspaceData = JSON.parse(window.atob(pluginspaceDataLocal));
      if (
        window.location.pathname.split("/").length > 1 &&
        window.location.pathname.split("/")[1] ===
          window.btoa(pluginspaceData.subdomain)
      )
        setPluginspace(JSON.parse(window.atob(pluginspaceDataLocal)));
      const pluginspace = JSON.parse(window.atob(pluginspaceDataLocal));

      return pluginspace;
    }
    return await api
      .get(`/api/pluginspace/this`, {
        headers: {
          Authorization: `Bearer ${otherToken}`,
        },
      })
      .then(async (response) => {
        if (response.data && response.data.data && response.data.data[0]) {
          localStorage.setItem(
            "this_pg",
            window.btoa(JSON.stringify(response.data.data[0]))
          );

          setPluginspace(response.data.data[0]);

          return response.data.data[0];
        } else {
          return null;
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function registerAccess(
    pageName: string,
    cpfIndicator?: string,
    productIndicator?: number,
    urlBrowsed?: string
  ) {
    return await api
      .post(
        `/api/accessCount/registerAccess`,
        {
          pageName,
          cpfIndicator,
          productIndicator,
          urlBrowsed,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then(async () => {
        return true;
      })
      .catch((err) => {
        return false;
      });
  }

  async function getVerifyShift() {
    if (shifts && shifts.length > 0) {
      return verifyShift(shifts);
    } else {
      try {
        const response = await api.get(`api/location-operation`, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        });
        if (response.data.data) {
          setShifts(response.data.data);
          return verifyShift(response.data.data);
        } else {
          return false;
        }
      } catch (error) {
        setShifts(null);
        return false;
      }
    }
  }

  function isTimeBetween(
    time: string,
    startTime: string,
    endTime: string
  ): boolean {
    const format = "HH:mm:ss";
    const timeMoment = moment(time, format);
    const startMoment = moment(startTime, format);
    const endMoment = moment(endTime, format);

    // Caso o intervalo atravesse a meia-noite
    if (endMoment.isBefore(startMoment)) {
      return (
        timeMoment.isBetween(startMoment, moment("23:59:59", format)) ||
        timeMoment.isBetween(moment("00:00:00", format), endMoment)
      );
    } else {
      return timeMoment.isBetween(startMoment, endMoment, null, "[]"); // Inclui os limites
    }
  }

  async function verifyShift(shiftsToVerify: ShiftType[]) {
    const pg = await getThisPluginspace();
    const shift = shiftsToVerify.find(
      (shiftItem) => shiftItem.weekday === new Date().getDay()
    );
    if (!shift) {
      return false;
    }
    let success = false;
    const gtmValue =
      !pg || !pg.gmt.split(":")[0] ? -3 : parseInt(pg.gmt.split(":")[0]);
    const now = moment();
    now.subtract(now.parseZone().utcOffset(), "minutes");
    let timeStartHourGtm =
      parseInt(shift.timeStart.split(":")[0]) + gtmValue * -1;
    let timeStart = `${
      timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
    }:${shift.timeStart.split(":")[1]}:${shift.timeStart.split(":")[2]}`;
    timeStart =
      parseInt(timeStart[0] + timeStart[1]) >= 24
        ? `${
            parseInt(timeStart[0] + timeStart[1]) - 24 > 9
              ? parseInt(timeStart[0] + timeStart[1]) - 24
              : `0${parseInt(timeStart[0] + timeStart[1]) - 24}`
          }:${shift.timeStart.split(":")[1]}:${shift.timeStart.split(":")[2]}`
        : timeStart;

    timeStartHourGtm = parseInt(shift.timeStart2.split(":")[0]) + gtmValue * -1;
    let timeStart2 = `${
      timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
    }:${shift.timeStart2.split(":")[1]}:${shift.timeStart2.split(":")[2]}`;
    timeStart2 =
      parseInt(timeStart2[0] + timeStart2[1]) >= 24
        ? `${
            parseInt(timeStart2[0] + timeStart2[1]) - 24 > 9
              ? parseInt(timeStart2[0] + timeStart2[1]) - 24
              : `0${parseInt(timeStart2[0] + timeStart2[1]) - 24}`
          }:${shift.timeStart2.split(":")[1]}:${shift.timeStart2.split(":")[2]}`
        : timeStart2;

    timeStartHourGtm = parseInt(shift.timeEnd.split(":")[0]) + gtmValue * -1;
    let timeEnd = `${
      timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
    }:${shift.timeEnd.split(":")[1]}:${shift.timeEnd.split(":")[2]}`;
    timeEnd =
      parseInt(timeEnd[0] + timeEnd[1]) >= 24
        ? `${
            parseInt(timeEnd[0] + timeEnd[1]) - 24 > 9
              ? parseInt(timeEnd[0] + timeEnd[1]) - 24
              : `0${parseInt(timeEnd[0] + timeEnd[1]) - 24}`
          }:${shift.timeEnd.split(":")[1]}:${shift.timeEnd.split(":")[2]}`
        : timeEnd;

    timeStartHourGtm = parseInt(shift.timeEnd2.split(":")[0]) + gtmValue * -1;
    let timeEnd2 = `${
      timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
    }:${shift.timeEnd2.split(":")[1]}:${shift.timeEnd2.split(":")[2]}`;
    timeEnd2 =
      parseInt(timeEnd2[0] + timeEnd2[1]) >= 24
        ? `${
            parseInt(timeEnd2[0] + timeEnd2[1]) - 24 > 9
              ? parseInt(timeEnd2[0] + timeEnd2[1]) - 24
              : `0${parseInt(timeEnd2[0] + timeEnd2[1]) - 24}`
          }:${shift.timeEnd2.split(":")[1]}:${shift.timeEnd2.split(":")[2]}`
        : timeEnd2;
    const nowTime = `${now.hours() > 9 ? now.hours() : `0${now.hours()}`}:${
      now.minutes() > 9 ? now.minutes() : `0${now.minutes()}`
    }:00`;

    if (
      moment(nowTime, "hh:mm:ss").isBetween(
        moment(timeStart, "hh:mm:ss"),
        moment(timeEnd, "hh:mm:ss")
      ) ||
      isTimeBetween(nowTime, timeStart, timeEnd)
    ) {
      success = true;
    } else if (
      moment(nowTime, "hh:mm:ss").isBetween(
        moment(timeStart2, "hh:mm:ss"),
        moment(timeEnd2, "hh:mm:ss")
      ) ||
      isTimeBetween(nowTime, timeStart2, timeEnd2)
    ) {
      success = true;
    } else if (
      parseInt(timeEnd[0] + timeEnd[1]) >= 0 &&
      parseInt(timeStart[0] + timeStart[1]) >= parseInt(timeEnd[0] + timeEnd[1])
    ) {
      if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment(timeStart, "hh:mm:ss"),
          moment("23:59:59", "hh:mm:ss"),
          "minutes"
        )
      ) {
        success = true;
      } else if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment("00:00:00", "hh:mm:ss"),
          moment(timeEnd, "hh:mm:ss"),
          "minutes"
        )
      ) {
        success = true;
      }
    } else if (
      parseInt(timeEnd2[0] + timeEnd2[1]) >= 0 &&
      parseInt(timeStart2[0] + timeStart2[1]) >=
        parseInt(timeEnd2[0] + timeEnd2[1])
    ) {
      if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment(timeStart2, "hh:mm:ss"),
          moment("00:00:00", "hh:mm:ss")
        )
      ) {
        success = true;
      } else if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment("00:00:00", "hh:mm:ss"),
          moment(timeEnd2, "hh:mm:ss")
        )
      ) {
        success = true;
      }
    }
    return success;
  }

  async function getLocations() {
    if (locations && locations.length > 0) {
      return locations;
    }

    return await api
      .get(`/api/location`, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const newLocations = response.data.data.map(
          (location: LocationType) => ({
            id: location.id,
            name: location.name,
            code: location.code,
            status: location.status,
            type: location.type,
            cep: location.cep,
            address: location.address,
          })
        );
        const newLocationsFiltered = newLocations.filter(
          (loc: LocationType) => loc.status === true
        );
        setLocations(newLocationsFiltered);

        localStorage.setItem("locations", JSON.stringify(newLocationsFiltered));

        return newLocationsFiltered;
      })
      .catch((err) => {
        console.log(err);
        return [];
      });
  }

  async function getData() {
    await Promise.all([getLocations(), getThisPluginspace()]);
  }

  useEffect(() => {
    getData();
  }, []);

  return (
    <PluginspaceContext.Provider
      value={{
        token,
        setToken,
        pluginspaceData,
        setPluginspaceData,
        getToken,
        registerAccess,
        getThisPluginspace,
        getVerifyShift,
        getLocations,
        locations,
        pluginspace,
        subDomain,
      }}
    >
      {props.children}
    </PluginspaceContext.Provider>
  );
}
